import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Brain, Dumbbell, Baby, Users, Leaf, HeartPulse, Check } from "lucide-react";

/**
 * Premium Preferences section.
 * - Essensprofil (persona) presets at the top — exactly 6 chips
 * - 4 quadrants of tags below, with active tags distributed across all 4
 *   when no persona is active; persona-selection highlights its preset tags.
 */

// Default active tags — distributed over all 4 quadrants (no-persona state).
const DEFAULT_ACTIVE = new Set([
  "Frische Zutaten",
  "Regionale Zutaten",
  "Hochwertige Fette",
  "Frisch gekocht",
  "Glutenfreie Optionen",
  "Allergen-Transparenz",
  "Bio-/Naturstandard",
  "Individuell anpassbar",
]);

// Persona → set of tag-labels that get pre-selected as preset.
// Labels must match the existing 36 preference labels exactly (see i18n criteria.groups).
const PERSONA_TAGS = {
  biohacker: new Set([
    "Zutatenqualität",
    "Frische Zutaten",
    "Bio-/Naturstandard",
    "Wenige Zusatzstoffe",
    "Transparente Herkunft",
    "Seed-oil-free",
    "Ohne industrielle Saatenöle",
    "Hochwertige Fette",
    "Olivenöl · Butter · Ghee",
    "Fermentierte Lebensmittel",
    "Nicht hochverarbeitet",
    "Low-Carb geeignet",
    "Zuckerarme Optionen",
    "Proteinreich",
    "Clean Eating",
    "Allergen-Transparenz",
  ]),
  gymbro: new Set([
    "Proteinreich",
    "Hochwertige tierische Produkte",
    "Zutatenqualität",
    "Frische Zutaten",
    "Hochwertige Fette",
    "Nicht hochverarbeitet",
    "Low-Carb geeignet",
    "Zuckerarme Optionen",
    "Frisch gekocht",
    "Hausgemacht",
    "Individuell anpassbar",
  ]),
  schwanger: new Set([
    "Frische Zutaten",
    "Zutatenqualität",
    "Transparente Herkunft",
    "Bio-/Naturstandard",
    "Wenige Zusatzstoffe",
    "Frisch gekocht",
    "Schonend zubereitet",
    "Nicht hochverarbeitet",
    "Allergen-Transparenz",
    "Individuell anpassbar",
    "Hochwertige tierische Produkte",
    "Saisonale Zutaten",
    "Regionale Zutaten",
  ]),
  familie: new Set([
    "Frische Zutaten",
    "Zutatenqualität",
    "Regionale Zutaten",
    "Saisonale Zutaten",
    "Bio-/Naturstandard",
    "Wenige Zusatzstoffe",
    "Hausgemacht",
    "Frisch gekocht",
    "Nicht hochverarbeitet",
    "Allergen-Transparenz",
    "Individuell anpassbar",
    "Vegetarisch-freundlich",
  ]),
  pflanzlich: new Set([
    "Vegan-freundlich",
    "Vegetarisch-freundlich",
    "Zutatenqualität",
    "Frische Zutaten",
    "Regionale Zutaten",
    "Saisonale Zutaten",
    "Bio-/Naturstandard",
    "Wenige Zusatzstoffe",
    "Nicht hochverarbeitet",
    "Hausgemacht",
    "Clean Eating",
    "Allergen-Transparenz",
    "Individuell anpassbar",
  ]),
  sensibel: new Set([
    "Glutenfreie Optionen",
    "Laktosefreie Optionen",
    "Histaminbewusste Optionen",
    "Allergen-Transparenz",
    "Individuell anpassbar",
    "Zutatenqualität",
    "Transparente Herkunft",
    "Wenige Zusatzstoffe",
    "Frisch gekocht",
    "Schonend zubereitet",
    "Nicht hochverarbeitet",
    "Clean Eating",
  ]),
};

const PERSONAS = [
  { id: "biohacker",  label: "Biohacker",              Icon: Brain },
  { id: "gymbro",     label: "Gymbro",                 Icon: Dumbbell },
  { id: "schwanger",  label: "Schwanger",              Icon: Baby },
  { id: "familie",    label: "Familie",                Icon: Users },
  { id: "pflanzlich", label: "Pflanzlich bewusst",     Icon: Leaf },
  { id: "sensibel",   label: "Sensibel & verträglich", Icon: HeartPulse },
];

export default function PreferencesSection({ groups, label, h2a, h2b, sub }) {
  const [persona, setPersona] = useState(null);

  const presetTags = useMemo(() => {
    if (!persona) return new Set();
    return PERSONA_TAGS[persona] || new Set();
  }, [persona]);

  const fadeUpView = (delay = 0) => ({
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <section className="section-pad-sm" data-testid="criteria-section">
      <div className="max-w-[1200px] mx-auto px-5 lg:px-12">
        <div className="max-w-[760px] mb-10">
          <motion.div {...fadeUpView()} className="section-label">{label}</motion.div>
          <motion.h2 {...fadeUpView(0.05)} className="display-md">
            <span className="block">{h2a}</span>
            <span className="block italic">{h2b}</span>
          </motion.h2>
          <motion.p {...fadeUpView(0.1)} className="body-lg mt-5">{sub}</motion.p>
        </div>

        {/* PERSONA PRESETS */}
        <motion.div
          {...fadeUpView(0.15)}
          className="persona-row"
          data-testid="persona-row"
          role="radiogroup"
          aria-label="Essensprofil wählen"
        >
          <span className="persona-row-label">Essensprofil wählen</span>
          <div className="persona-chips">
            {PERSONAS.map(({ id, label: l, Icon }) => {
              const active = persona === id;
              return (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  data-testid={`persona-${id}`}
                  onClick={() => setPersona(active ? null : id)}
                  className={`persona-chip ${active ? "is-active" : ""}`}
                >
                  <Icon size={15} strokeWidth={1.75} className="persona-chip-icon" />
                  <span>{l}</span>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* 4 QUADRANTS */}
        <div className="grid md:grid-cols-2 gap-6 mt-8" data-testid="criteria-groups">
          {groups.map((g, gi) => (
            <motion.div
              key={gi}
              {...fadeUpView(gi * 0.06)}
              className="card-standard"
              data-testid={`criteria-group-${gi}`}
            >
              <div className="font-mono text-[12px] text-accent mb-3">
                0{gi + 1} · {g.label.toUpperCase()}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {g.tags.map((tag, ti) => {
                  const isDefault = DEFAULT_ACTIVE.has(tag);
                  const isPreset = presetTags.has(tag);
                  const cls = isPreset
                    ? "tag-chip preset"
                    : isDefault && !persona
                    ? "tag-chip active"
                    : "tag-chip";
                  return (
                    <span
                      key={tag + ti}
                      className={cls}
                      data-active={isPreset || (isDefault && !persona) ? "true" : "false"}
                    >
                      {isPreset && (
                        <Check size={11} strokeWidth={2.5} className="inline-block mr-1 -mt-px" />
                      )}
                      {tag}
                    </span>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
